import {
  Box,
  Icon,
  MenuCheckboxItem,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  Text,
} from "@salestrak/ui";

import { useAppData } from "#app/lib/data";
import { useModuleRail } from "#app/components/navigation/module-rail/module-rail-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";

/**
 * The Workflows module-title menu — the popup behind the pane header's
 * "Workflows ⌄" trigger (`ModuleMenu titleMenu`). Module-scope actions and the
 * sidebar workflow-list scope toggles:
 *
 *  - Manage Workflows (the `/workflows` roster page).
 *  - "Show all workflows": a privileged viewer (Owner / admin-override,
 *    `me.seesAllWorkflows`) sees every workflow the server surfaces; off
 *    (the default) scopes the sidebar down to workflows their own grants
 *    reach (`viewerHasExplicitAccess`). Offered ONLY to that viewer — for
 *    everyone else the two sets are identical, so there is no "all" to
 *    show and the row is omitted rather than shown disabled. It stays
 *    enabled even when the two sets happen to coincide right now, the same
 *    way "Show archived" is live with nothing archived.
 *  - "Show archived": archived workflows stay in the nav only when opted
 *    into. Off by default.
 *
 * Creating a workflow is NOT here: the header's always-visible `+ ⌄` create
 * popover owns that action, and this menu duplicating it is exactly what the
 * title-menu move removed.
 *
 * The toggles persist per (user, workspace) on `myPreferences` and are read
 * through the shared `useModuleRail` instance so the list filters the moment
 * a switch flips.
 */
export function WorkflowSettingsMenuPopup() {
  const { push: navigate } = useAppNavigate();
  const { currentUser } = useAppData();
  const {
    showAllWorkflows,
    setShowAllWorkflows,
    showArchivedWorkflows,
    setShowArchivedWorkflows,
  } = useModuleRail();

  const seesAllWorkflows = currentUser?.seesAllWorkflows ?? false;

  return (
    // Hangs from the title's leading edge — the trigger is the leftmost thing
    // in the header, so `start` keeps the popup over the pane, not the page.
    <MenuPopup align="start" side="bottom" width="panel">
      <MenuItem onClick={() => navigate("/workflows")}>
        <Icon color="muted" name="layout-grid" size="md" />
        <Text>Manage Workflows</Text>
      </MenuItem>
      <MenuSeparator />
      {seesAllWorkflows ? (
        <MenuCheckboxItem
          checked={showAllWorkflows}
          closeOnClick={false}
          onCheckedChange={setShowAllWorkflows}
          variant="switch"
        >
          <Box align="center" direction="row" gap="sm">
            <Icon color="muted" name="eye" size="sm" />
            <Text>Show all workflows</Text>
          </Box>
        </MenuCheckboxItem>
      ) : null}
      <MenuCheckboxItem
        checked={showArchivedWorkflows}
        closeOnClick={false}
        onCheckedChange={setShowArchivedWorkflows}
        variant="switch"
      >
        <Box align="center" direction="row" gap="sm">
          <Icon color="muted" name="archive" size="sm" />
          <Text>Show archived</Text>
        </Box>
      </MenuCheckboxItem>
    </MenuPopup>
  );
}
